import React, { useState, useEffect, useMemo } from "react";
import { ISingleBarProps, ICapsuleData } from './type.d';
import { LIUBAI_PERCENT } from './constants';
import { getMaxValue, getDataDirectionType } from './util';
import Capsule from "./Capsule";
import { mathFormat } from '@sg/utils/common';
import Mark from "./Mark";


export default function SingleBar(props: ISingleBarProps) {
  const {
    data,
    height = 40,
    coefficient = 1,
    directionType,
    maxValue,
    showZeroText = true,
    valueFormatter,
  } = props;
  const [pillarsData, setPillarsData] = useState<ICapsuleData[]>([]);
  const [totalPoint, setTotalPoint] = useState<ICapsuleData>({
    title: '',
    value: 0,
    rate: 0,
    type: 'total'
  });
  const [zeroPoint, setZeroPoint] = useState<ICapsuleData>({
    title: '',
    value: 0,
    rate: 0,
    type: 'zero'
  });
  const computedMaxValue = maxValue ?? getMaxValue(data);
  const hasNegative = useMemo(() => pillarsData?.some(item => item?.value < 0), [pillarsData]);
  const hasData = useMemo(
    () =>
      pillarsData?.some(
        (item) =>
          item?.value !== null && item?.value !== undefined && !item?.isHide,
      ),
    [pillarsData],
  );
  // 获取分类数据
  const setClassificationData = () => {
    const tmpPillarsData: ICapsuleData[] = [];
    let tmpTotalData: ICapsuleData = {
      title: '',
      value: 0,
      rate: 0,
      type: 'total'
    };
    let tmpZeroPointData: ICapsuleData = {
      title: '',
      value: 0,
      rate: 0,
      type: 'zero'
    };
    
    // Check if any pillar has valid data so we know whether to auto-hide placeholders
    const hasValidData = data?.some(item => 
      item.type === 'pillar' && item.value !== null && item.value !== undefined
    );
    
    data?.forEach(item => {
      // Auto-hide items when no valid pillar data exists (except zero mark)
      const shouldAutoHide = !hasValidData && item.type !== 'zero';
      const shouldHide = item.isHide !== undefined 
        ? item.isHide  // Respect explicit isHide if provided
        : shouldAutoHide;
      
      // Prefer capsules with preformatted text (already converted to millions).
      const formattedValue =
        item.formattedValue ??
        (valueFormatter
          ? valueFormatter(item.value as number)
          : item.value === 0
            ? '0M'
            : mathFormat(item.value, item.precision));

      const newItem = {
        ...item,
        formattedValue,
        isHide: shouldHide,
      };
      
      if (item.type === 'pillar') {
        tmpPillarsData.push(newItem);
      } else if (item.type === 'total') {
        tmpTotalData = {
          ...newItem,
          liubaiValue: newItem.value * Number(`1.0${LIUBAI_PERCENT}`),
        };
      } else if (item.type === 'zero') {
        tmpZeroPointData = newItem;
      }
    });
    setPillarsData(tmpPillarsData);
    setTotalPoint(tmpTotalData);
    setZeroPoint(tmpZeroPointData);
  };

  const dataDirection = useMemo(() => getDataDirectionType(pillarsData, totalPoint), [pillarsData, totalPoint]);

  const getTotalPointPosition = (data: ICapsuleData, hasNegative: boolean) => {
    const value = data?.value ?? 0;
    const trueMaxValue =
      (computedMaxValue || Math.abs(value) || 1) * coefficient;

    // Centered mode: zero sits at 50%, values spread ± from center
    // Used when layout is centerInSameDirection, or data has negative values
    const useCentered =
      directionType === 'centerInSameDirection' || hasNegative || value < 0;

    if (useCentered) {
      return 50 + (value / trueMaxValue) * 50;
    }

    // Left-aligned mode: zero sits at 0%, values spread 0–100%
    return (value / trueMaxValue) * 100;
  };

  useEffect(() => {
    setClassificationData();
  }, [data]);

  return (
    <div className="capsule-single-bar" style={{height: `${height || 40}px`}}>
      {
        pillarsData?.length && <div>
          <Mark
            key={`${zeroPoint?.title}_${Math.abs(zeroPoint?.value)}_${Math.abs(pillarsData[0]?.value)}`}
            data={zeroPoint}
            position={dataDirection === 'mixed' || directionType === 'centerInSameDirection' ? 50 : 0}
          />
          {hasData ? (
            <>
              <Capsule
                data={pillarsData}
                totalData={totalPoint}
                coefficient={coefficient}
                height={height}
                dataDirection={dataDirection}
                directionType={directionType}
                showZeroText={showZeroText}
              />
              <Mark
                key={`${totalPoint?.title}_${Math.abs(totalPoint?.value)}_${Math.abs(pillarsData[0]?.value)}`}
                data={totalPoint}
                position={getTotalPointPosition(totalPoint, hasNegative)}
              />
            </>
          ) : (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height,
                width: '100%',
                background: '#F2F3F5',
                borderRadius: '0 30px 30px 0',
              }}
            />
          )}
        </div>
      }
    </div>
  );
}